import type { Statut, TypeDemande } from '@/features/domaine/types/opco'
import { ensureSuccessStatusCode, useFetch } from '@/features/fetch'
import type {
  Search,
  SearchCodeDiplome,
  SearchEtablissementAdherent,
  SearchEtablissementPrestataire,
  SearchFormacode,
  SearchIdcc,
  SearchNsf,
  SearchRncp,
  SearchRs,
  SearchCommune
} from './DemandeAllService.types'
import type { EtablissementAdherentDTO, EtablissementPrestataireDTO } from '@/features/api/extranet/dto/shared'
import type { IResponse } from '@/features/api/extranet'
import type { SubmitDemandeDTO } from '@/features/api/extranet/dto/Demande/shared/SubmitDemandeDTO'
import type { CommuneDto, LovItemDTO, RncpDto } from '@/features/api/extranet/dto/Lov'
import { isNullUndefinedOrEmptyString, urlEncodeQuery, type KeyObject } from '@/features/shared/tools'
import type {
  DemandeCloneDTO,
  DemandeCreateDTO,
  DemandeCreateResultDTO,
  HabilitationsDTO,
  SalarieRechercheDTO,
  TuteurRechercheDTO,
  ValidateNirDto,
  ValidateNirQuery,
} from '@/features/api/extranet/dto/Demande/shared'

import type { RechercheSalarieMode } from '@/features/interface/demande/shared/components/salaries/RechercheSalarie.type'
import { demandeToLocation } from '@/features/application/locations'
import {
  type NavigateToDemandeExtranet,
  type NavigateToLocation,
} from '@/router/navigateTo'
import { locationToEndPointsDemande } from '@/features/services'

const INIT_DEFAULT: RequestInit = { sablier: true, dialogMode: 'retry' } as const
const INIT_DEFAULT_POST: RequestInit = { ...INIT_DEFAULT, reloadPageOnConcurrencyException: true } as const

async function deleteDemande(location: NavigateToDemandeExtranet, init?: RequestInit) {
  init = {
    ...INIT_DEFAULT,
    ...init,
    method: 'DELETE',
  }

  // Supprime la demande
  const fetch = useFetch('extranet')
  const path = locationToEndPointsDemande(location).endPointEntite
  const response = await fetch(`${path}/${location.id}`, init)
  ensureSuccessStatusCode(response)
}

export async function validateNir(nir: string, nom: string, init?: RequestInit): Promise<ValidateNirDto> {
  const query: ValidateNirQuery = { Nir: nir, Nom: nom }

  init = {
    ...{ sablier: false, dialogMode: 'silent', appCache: true },
    ...init,
    method: 'POST',
    body: JSON.stringify(query),
    blocageImpersoDisabled: true,
  }

  const fetch = useFetch('extranet')

  const response = await fetch('/validate/nir', init)

  ensureSuccessStatusCode(response)

  const result = (await response.json()) as { data: ValidateNirDto }

  return result.data
}

function buildSearchEtablissement<T extends 'adherent' | 'prestataire'>(entite: T) {
  type TResult = T extends 'adherent' ? EtablissementAdherentDTO : EtablissementPrestataireDTO
  type TSearch = T extends 'adherent' ? SearchEtablissementAdherent : SearchEtablissementPrestataire

  return async function (search: string | TSearch, init?: RequestInit): Promise<TResult[]> {
    init = {
      ...INIT_DEFAULT,
      ...init,
    }

    // Transforme la recherche en TSearch
    if (typeof search === 'string') {
      search = {
        search,
      } as TSearch
    }

    const { dispositif, ...searchArgs } = search as TSearch

    const queryString = urlEncodeQuery(searchArgs)

    const fetch = useFetch('extranet')

    const url = dispositif
      ? `/formations/demandes/${entite}/${dispositif.toLocaleLowerCase()}${queryString}`
      : `/formations/demandes/${entite}${queryString}`

    const response = await fetch(url, init)
    ensureSuccessStatusCode(response)

    const result = (await response.json()) as IResponse<TResult[]>

    return result.data
  }
}

const searchEtablissementAdherent = buildSearchEtablissement('adherent')
const searchEtablissementPrestataire = buildSearchEtablissement('prestataire')

async function getRncp(code: string, init?: RequestInit) {
  init = {
    ...INIT_DEFAULT,
    ...init,
  }

  const fetch = useFetch('extranet')
  const response = await fetch(`/lov/rncp/${code}`, init)
  ensureSuccessStatusCode(response)

  const result = (await response.json()) as IResponse<RncpDto>

  return result.data
}

export function getRncpSilent(code: string) {
  return getRncp(code, {
    sablier: false,
    dialogMode: 'silent',
    appCache: true,
  })
}

async function getHabilitations(typeDemande: TypeDemande, siret: string, idcc: string, init?: RequestInit) {
  init = {
    ...INIT_DEFAULT,
    ...init,
    method: 'POST',
    body: JSON.stringify({
      siret,
      idcc,
    }),
    blocageImpersoDisabled: true,
  }

  const fetch = useFetch('extranet')
  const response = await fetch(`/${typeDemande.toLowerCase()}/demandes/habilitation`, init)
  ensureSuccessStatusCode(response)

  const result = (await response.json()) as IResponse<HabilitationsDTO>

  return result.data
}

const searchRncp = buildSearch<SearchRncp>('/lov/rncp')
const searchCodeDiplome = buildSearch<SearchCodeDiplome>('/lov/codediplome')
const searchFormacode = buildSearch<SearchFormacode>('/lov/formacode')
const searchNsf = buildSearch<SearchNsf>('/lov/nsf')
const searchIdcc = buildSearch<SearchIdcc>('/lov/idcc')
const searchRs = buildSearch<SearchRs>('/lov/rs')
const searchCommune = buildSearch<SearchCommune, CommuneDto>('/lov/commune')

function buildSearch<TSearch extends Search, TResult extends LovItemDTO<string> = LovItemDTO<string>>(path: string) {
  return async function (search: string | TSearch, init?: RequestInit): Promise<Array<TResult>> {
    init = {
      ...INIT_DEFAULT,
      ...init,
    }
    const queryString = buildSearchQueryString(search)

    const fetch = useFetch('extranet')
    const response = await fetch(`${path}?${queryString}`, init)
    ensureSuccessStatusCode(response)

    const result = (await response.json()) as IResponse<Array<TResult>>

    return result.data
  }
}

const buildSearchQueryString = (search: string | Search) =>
  typeof search === 'string'
    ? `search=${search}`
    : Object.entries(search)
      .filter(([_, value]) => !isNullUndefinedOrEmptyString(value))
      .map(([key, value]) => `${key}=${value}`)
      .join('&')

async function addDemande(
  payload: DemandeCreateDTO | DemandeCloneDTO,
  init?: RequestInit,
): Promise<DemandeCreateResultDTO> {
  init = {
    ...INIT_DEFAULT,
    ...init,
    method: 'POST',
    body: JSON.stringify(payload),
  }

  const location = demandeToLocation({
    typeDemande: payload.typeDemande,
    dispositif: payload.dispositif,
    sousDispositif: (payload as any).sousDispositif,
  })
  const path = locationToEndPointsDemande(location).endPointEntite

  // Création de la demande
  const fetch = useFetch('extranet')
  const response = await fetch(path, init)
  ensureSuccessStatusCode(response)

  const result = (await response.json()) as IResponse<DemandeCreateResultDTO>

  return result.data
}

async function submitDemande<T extends SubmitDemandeDTO>(statut: Statut, data: T, init?: RequestInit) {
  if (!init) init = INIT_DEFAULT_POST

  const request: RequestInit = {
    ...init,
    method: 'POST',
    body: JSON.stringify({
      id: data.id,
      statut: statut,
      rowVersion: data.rowVersion,
    }),
  }

  // Stocke la demande
  const fetch = useFetch('extranet')

  const response = await fetch(`/${data.typeDemande.toLowerCase()}/demandes/${data.id}/submit`, request)
  ensureSuccessStatusCode(response)
}

async function importFile(file: File, init?: RequestInit) {
  if (!init) init = INIT_DEFAULT

  const data = new FormData()
  data.append('formFile', file)

  const request: RequestInit = {
    ...init,
    method: 'POST',
    body: data,
    noDefaultContentType: true,
  }

  // Stocke le fichier
  const fetch = useFetch('extranet')

  const response = await fetch(`/fichiers`, request)
  ensureSuccessStatusCode(response)
  const result = (await response.json()) as IResponse<string>

  return result.data
}

async function exportFile(typeDemande: TypeDemande, idDemande: number, idFichier: string, init?: RequestInit): Promise<Blob> {
  init = {
    ...INIT_DEFAULT,
    ...init,
  }

  const fetch = useFetch('extranet')
  const response = await fetch(`/${typeDemande.toLowerCase()}/demandes/${idDemande}/download/${idFichier}`, init)
  ensureSuccessStatusCode(response)

  return await response.blob()
}

async function loadLovWithLocation<TLov extends object>(
  location: KeyObject<NavigateToLocation>,
  init?: RequestInit,
) {
  init = {
    ...{ sablier: true, dialogMode: 'retry', appCache: true },
    ...init,
  }

  // Charge les LOV
  const fetch = useFetch('extranet')
  const path = locationToEndPointsDemande(location).endPointLov
  const response = await fetch(path, init)
  ensureSuccessStatusCode(response)

  return (await response.json()) as TLov
}

function buildRechercheSalaries<Mode extends RechercheSalarieMode>(mode: Mode) {
  type TResult = Mode extends 'salaries' ? SalarieRechercheDTO : TuteurRechercheDTO

  return async function (demandeId: number, search: string, init?: RequestInit): Promise<TResult[]> {
    const INIT_DEFAULT: RequestInit = { sablier: true, dialogMode: 'retry' } as const
    init = {
      ...INIT_DEFAULT,
      ...init,
    }

    const fetch = useFetch('extranet')

    const query = `?search=${search}`
    const url = `/formation/demandes/${demandeId}/${mode}${query}`

    const response = await fetch(url, init)
    ensureSuccessStatusCode(response)

    const result = (await response.json()) as IResponse<TResult[]>
    return result.data
  }
}
export const searchSalarie = buildRechercheSalaries('salaries')
export const searchTuteur = buildRechercheSalaries('tuteurs')

export type RncpHabilitation = 'Organiser' | 'Former' | 'DeDroit'

export async function validateRncpHabilitations(
  siret: string,
  rncp: string,
  init?: RequestInit,
): Promise<RncpHabilitation[]> {
  init = {
    ...{ sablier: false, dialogMode: 'silent', appCache: true },
    ...init,
  }

  const fetch = useFetch('extranet')
  const response = await fetch(`/validate/rncp/habilitations?Siret=${siret}&Rncp=${rncp}`, init)
  ensureSuccessStatusCode(response)

  const result = (await response.json()) as { data: RncpHabilitation[] }
  return result.data
}

export default {
  addDemande,
  deleteDemande,
  exportFile,
  getHabilitations,
  getRncp,
  getRncpSilent,
  importFile,
  loadLovWithLocation,
  searchCodeDiplome,
  searchEtablissementAdherent,
  searchEtablissementPrestataire,
  searchFormacode,
  searchIdcc,
  searchNsf,
  searchRncp,
  searchRs,
  searchCommune,
  searchSalarie,
  submitDemande,
  validateNir,
  validateRncpHabilitations,
}
